import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { renderProfileCard } from '../lib/profile-card-image';
import { haptic } from '../lib/telegram';
import { money, serviceDuration } from '../lib/format';
import type { ArtistProfile, Schedule, Service, ShowcasedWork, User } from '../types';
import { Button, Card, ErrorNotice, Modal } from './ui';

interface RenderedCard {
  file: File;
  dataUrl: string;
}

/**
 * Ссылка и карточка профиля картинкой — для постов, сторис и чужих чатов, где
 * ссылку не откроют.
 *
 * Картинка живёт как `data:`-URL, а не `blob:`. Во встроенном браузере Telegram
 * blob-ссылка не открывается и не сохраняется: долгое нажатие даёт меню с
 * пустым адресом, а «Download» молча ничего не делает.
 */
export function ShareProfile({
  profile,
  publicUrl,
}: {
  profile: ArtistProfile;
  publicUrl: string;
}) {
  const [pending, setPending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [card, setCard] = useState<RenderedCard | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schedule = useQuery({
    queryKey: ['schedule'],
    queryFn: () => api<Schedule>('/artist/schedule'),
  });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<User>('/me') });
  /*
   * Услуги и работы — то, ради чего визитку и открывают: заказчик решает по
   * цене, сроку и картинкам, а не по описанию. Грузим их заранее, чтобы кнопка
   * не думала лишнюю секунду.
   */
  const services = useQuery({
    queryKey: ['services'],
    queryFn: () => api<{ items: Service[] }>('/services'),
  });
  const works = useQuery({
    queryKey: ['public-artist', profile.publicLink.slug, 'works'],
    queryFn: () =>
      api<{ items: ShowcasedWork[] }>(`/public/artists/${profile.publicLink.slug}/works`),
    retry: false,
  });

  const shareLink = async () => {
    setError(null);
    const done = await copyText(publicUrl);
    haptic(done ? 'success' : 'warning');
    setCopied(done);
    if (!done) setError('Не получилось скопировать — выделите ссылку вручную');
  };

  const buildCard = async () => {
    setPending(true);
    setError(null);

    try {
      const rendered = await renderProfileCard({
        profile,
        nextOpening: schedule.data?.nextOpening ?? null,
        // Почта попадает на картинку только если художник открыл её публично.
        contactEmail: profile.showContactEmail ? (me.data?.contactEmail ?? null) : null,
        publicUrl,
        services: (services.data?.items ?? [])
          .filter((service) => service.isActive && !service.isArchived)
          .slice(0, 3)
          .map((service) => ({
            title: service.title,
            price: service.basePrice ? money(service.basePrice, service.currency) : null,
            duration: serviceDuration(service),
          })),
        works: (works.data?.items ?? [])
          .map((work) => work.previewUrl)
          .filter((url): url is string => url !== null),
      });

      setCard({
        file: new File([rendered.blob], `artflow-${profile.publicLink.slug}.png`, {
          type: 'image/png',
        }),
        dataUrl: rendered.dataUrl,
      });
    } catch {
      setError('Не удалось собрать картинку');
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Card className="stack stack--compact">
        <div className="share-actions">
          <Button variant="secondary" onClick={() => void shareLink()}>
            {copied ? 'Скопировано' : 'Копировать ссылку'}
          </Button>
          <Button variant="secondary" disabled={pending} onClick={() => void buildCard()}>
            {pending ? 'Собираем…' : 'Картинкой'}
          </Button>
        </div>
        <p className="muted settings-note">
          На картинке — статус, ближайшее окно, услуги с ценами, работы и теги.
        </p>
        {error && <span className="field__error">{error}</span>}
        {schedule.error && <ErrorNotice error={schedule.error} />}
      </Card>

      {card && <ProfileCardPreview card={card} onClose={() => setCard(null)} />}
    </>
  );
}

function ProfileCardPreview({ card, onClose }: { card: RenderedCard; onClose: () => void }) {
  const [failed, setFailed] = useState(false);
  const canShare = navigator.canShare?.({ files: [card.file] }) ?? false;

  const share = async () => {
    setFailed(false);
    try {
      await navigator.share({ files: [card.file], title: card.file.name });
      onClose();
    } catch (error) {
      // Отмена системного окна — не ошибка: пользователь просто передумал.
      if (!(error instanceof DOMException && error.name === 'AbortError')) setFailed(true);
    }
  };

  return (
    <Modal title="Карточка профиля" onClose={onClose}>
      <div className="stack stack--compact">
        <img className="share-preview" src={card.dataUrl} alt="Карточка профиля" />
        <p className="muted settings-note">Долгое нажатие по картинке — сохранить в галерею.</p>
        {canShare && (
          <Button onClick={() => void share()}>{failed ? 'Повторить' : 'Поделиться'}</Button>
        )}
        <a
          className="button button--secondary"
          href={card.dataUrl}
          download={card.file.name}
          target="_blank"
          rel="noreferrer"
        >
          Открыть картинку
        </a>
      </div>
    </Modal>
  );
}
